import { Component } from '@theme/component';

/**
 * @typedef {Object} PtaContactFormRefs
 * @property {HTMLSelectElement} reason - Request-type selector
 * @property {HTMLFieldSetElement} groupAdapter - Adapter-request fields
 * @property {HTMLFieldSetElement} groupOrder - Order fields, shown for orders and returns alike
 * @property {HTMLFieldSetElement} groupReturn - Return-only fields
 * @property {HTMLSelectElement} battery - Battery platform selector
 * @property {HTMLSelectElement} tool - Tool platform selector
 * @property {HTMLElement} batteryOther - Free-text shown when battery is "other"
 * @property {HTMLElement} toolOther - Free-text shown when tool is "other"
 * @property {HTMLInputElement} referrer - Hidden field recording the previous page
 * @property {HTMLButtonElement} submit - Submit button
 * @property {HTMLElement} submitLabel - The submit button's text, swapped while posting
 * @property {HTMLElement} [success] - Confirmation banner, present only after a successful post
 * @property {HTMLElement} [errors] - Error banner, present only after a rejected post
 */

/**
 * Query-param aliases so links can read naturally, e.g. ?reason=returns
 *
 * `return`/`returns` used to land on the order branch because there was only one. They now
 * have their own, and the aliases are kept rather than dropped: anything already linking to
 * `?reason=returns` — an email, a policy page, a note to a customer — still arrives in the
 * right place, and now in a better one.
 */
const REASON_ALIASES = {
  adapter: 'adapter',
  product: 'adapter',
  order: 'order',
  orders: 'order',
  shipping: 'order',
  delivery: 'order',
  return: 'return',
  returns: 'return',
  exchange: 'return',
  refund: 'return',
  other: 'other',
  general: 'other',
};

/**
 * Shows only the fields relevant to the selected request type, and reveals a free-text
 * box when a brand is not in the list.
 *
 * Deep-linkable, so other pages can drop the customer straight into the right form:
 *
 *   /pages/contact-us?reason=returns
 *   /pages/contact-us?reason=adapter&tool=Makita%2018V%20LXT
 *   /pages/contact-us?order=1234&reason=returns
 *
 * Shopify Liquid cannot read query parameters, so this is applied client-side.
 *
 * It also prunes the form on submit, which is what keeps the notification email readable —
 * see `#pruneUnanswered`.
 *
 * @extends {Component<PtaContactFormRefs>}
 */
class PtaContactForm extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { reason, battery, tool, referrer } = this.refs;

    if (referrer) referrer.value = document.referrer || '';

    this.#applyUrlParams();

    reason?.addEventListener('change', () => this.#syncGroups());
    battery?.addEventListener('change', () => this.#syncOther('battery'));
    tool?.addEventListener('change', () => this.#syncOther('tool'));

    // Runs after constraint validation, so the required fields are known to be filled by
    // the time anything is disabled.
    this.querySelector('form')?.addEventListener('submit', () => {
      this.#reportReason();
      this.#pruneUnanswered();
      this.#enterBusyState();
    });

    window.addEventListener('pageshow', this.onPageShow);

    this.#syncGroups();
    this.#syncOther('battery');
    this.#syncOther('tool');

    /* Whichever banner the redirected document carries. Shopify sets one or the other, never both,
       so the `??` is a choice between two absent-or-present refs rather than a precedence rule.

       The error branch is the one this was missing. Its `role="alert"` cannot fire — the element is
       in the document at parse time, and a live region announces changes — so a rejected submission
       was completely silent until focus was moved onto the reason.

       `preventScroll`, because the fragment has already put the page where it belongs and a second
       scroll on load reads as a jump. */
    const banner = this.refs.success ?? this.refs.errors;

    banner?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Restore the button when the page comes back from the bfcache.
   *
   * The post is a full navigation, so going Back returns a document frozen mid-submit: the
   * button still disabled, still reading "Sending…", and nothing to un-freeze it, since a
   * restore does not re-run `connectedCallback`.
   *
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) this.#leaveBusyState();
  };

  /** Pre-select the request type and prefill fields from the query string. */
  #applyUrlParams() {
    const params = new URLSearchParams(window.location.search);

    if (![...params.keys()].length) return;

    const wanted = REASON_ALIASES[(params.get('reason') ?? '').toLowerCase()];

    if (wanted && this.refs.reason) this.refs.reason.value = wanted;

    // Selects only accept a value that exists as an option; inputs take anything.
    const prefill = [
      [this.refs.battery, params.get('battery')],
      [this.refs.tool, params.get('tool')],
      [this.querySelector('#pta-contact-model'), params.get('model')],
      [this.querySelector('#pta-contact-order'), params.get('order')],
    ];

    for (const [field, value] of prefill) {
      if (!field || !value) continue;

      if (field instanceof HTMLSelectElement) {
        const match = [...field.options].find((option) => option.value === value);

        if (match) field.value = value;
      } else if (field instanceof HTMLInputElement) {
        field.value = value;
      }
    }
  }

  /**
   * Show the fieldsets matching the selected request type, hide the rest.
   *
   * `data-reason` is a space-separated list rather than a single value, because the order
   * number is asked of both the order and the return branch and exists once.
   */
  #syncGroups() {
    const selected = this.refs.reason?.value ?? 'adapter';

    for (const group of this.querySelectorAll('[data-reason]')) {
      if (!(group instanceof HTMLElement)) continue;

      group.hidden = !(group.dataset.reason ?? '').split(' ').includes(selected);
    }
  }

  /**
   * Disable every control the customer did not answer, so it is not submitted at all.
   *
   * Shopify prints one labelled line per `contact[...]` field it receives, so a field that
   * arrives empty becomes a label with nothing after it. Two ways that happened:
   *
   * - `hidden` does not stop a control being submitted. A returns enquiry posted all five
   *   adapter fields as empty strings, and the email carried five dead labels.
   * - A field answered and then hidden again — pick "Other" for the battery, type a brand,
   *   then pick a listed platform — kept its value and posted it, contradicting the answer
   *   above it.
   *
   * A `[hidden]` ancestor covers both, since it catches the free-text wrappers as well as
   * the fieldsets. Disabled controls are omitted from the form data, which is the whole
   * mechanism: nothing needs to know what the email template does with them.
   *
   * Scoped to `contact[...]` deliberately. `{% form %}` emits `form_type` and `utf8`, and
   * Shopify's invisible captcha injects its own field whose token is written at submit
   * time — pruning an empty one of those would break the post rather than tidy the email.
   */
  #pruneUnanswered() {
    for (const field of this.querySelectorAll('[name^="contact["]')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
        continue;
      }

      if (field.value.trim() === '' || field.closest('[hidden]')) field.disabled = true;
    }
  }

  /**
   * Put the submit button into its posting state, and keep it there.
   *
   * There is no completion callback to pair with this: the form posts natively, so the only
   * thing that ends the busy state is the next document arriving. Disabling the button is
   * what stops a second POST from an impatient double-click — which on this form would send
   * the same enquiry twice.
   */
  #enterBusyState() {
    const { submit, submitLabel } = this.refs;

    if (!submit) return;

    submit.setAttribute('aria-busy', 'true');
    submit.disabled = true;

    const busyLabel = submit.dataset.labelBusy;

    if (busyLabel && submitLabel) {
      // Kept so the bfcache restore can put the original back.
      submitLabel.dataset.labelIdle = submitLabel.textContent ?? '';
      submitLabel.textContent = busyLabel;
    }
  }

  /**
   * Report which branch the enquiry came in on.
   *
   * The four request types are the store's support mix in one field, and nothing else measures
   * it: the reason reaches the notification email, but an inbox is not a report. Published
   * before the prune, since `#pruneUnanswered` disables controls and a disabled select still
   * reads its `value` but there is no reason to depend on that.
   *
   * See `assets/pta-track.js` for why this is `pta_interaction` rather than a name of its own,
   * and why it is a no-op until the "PTA Custom Events" pixel subscribes.
   */
  #reportReason() {
    const reason = this.refs.reason?.value;

    if (!reason) return;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    window.Shopify.analytics.publish('pta_interaction', {
      action: 'contact_reason',
      surface: 'contact_form',
      label: reason,
    });
  }

  /** Undo `#enterBusyState`. */
  #leaveBusyState() {
    const { submit, submitLabel } = this.refs;

    if (!submit) return;

    submit.removeAttribute('aria-busy');
    submit.disabled = false;

    const idleLabel = submitLabel?.dataset.labelIdle;

    if (idleLabel && submitLabel) submitLabel.textContent = idleLabel;
  }

  /**
   * @param {'battery'|'tool'} which
   */
  #syncOther(which) {
    const select = this.refs[which];
    const field = this.refs[which === 'battery' ? 'batteryOther' : 'toolOther'];

    if (!select || !field) return;

    field.hidden = select.value !== 'other';
  }
}

customElements.define('pta-contact-form', PtaContactForm);
